import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ProductCard from "@/components/ProductCard";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowLeft, ChevronLeft, ChevronRight, Package } from "lucide-react";

export default function BrandDetailPage() {
  const { id } = useParams();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { t, locale } = useLanguage();
  const isReseller = ["reseller", "agen", "mitra", "produsen", "distributor", "admin"].includes(user?.role);
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => {
    API.get(`/brands/${id}`).then((r) => setBrand(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  if (loading) return <div className="min-h-screen"><Header /><div className="flex items-center justify-center py-32"><div className="animate-pulse text-stone-400">{t("common.loading")}</div></div></div>;
  if (!brand) return <div className="min-h-screen"><Header /><div className="text-center py-32"><p className="text-stone-500">{t("common.no_brands")}</p></div></div>;

  const totalProducts = brand.products?.length || 0;
  const totalPages = Math.max(1, Math.ceil(totalProducts / itemsPerPage));
  const paginatedProducts = (brand.products || []).slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="min-h-screen" data-testid="brand-detail-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to="/brands" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-700 mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> {t("common.back_to_all_brands")}
        </Link>
        <div className="mb-12">
          <h1 className="font-serif text-3xl sm:text-4xl text-stone-900 mb-3" data-testid="brand-name">{brand.name}</h1>
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-stone-100 rounded text-xs font-semibold tracking-wide uppercase text-stone-600 mb-6">
            <Package className="w-4 h-4 text-stone-400" /> {totalProducts} {t("common.products")}
          </div>
          <p className="text-sm text-stone-500 max-w-xl leading-relaxed">{brand.description}</p>
        </div>

        {totalProducts > 0 ? (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
              {paginatedProducts.map((p) => (
                <ProductCard key={p.id} product={p} showReseller={isReseller} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-stone-100 pt-6 mt-12">
                <p className="text-sm text-stone-500">{t("common.page")} {currentPage} {t("common.of")} {totalPages}</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 border border-stone-200 rounded text-stone-500 hover:bg-stone-50 disabled:opacity-50 transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 border border-stone-200 rounded text-stone-500 hover:bg-stone-50 disabled:opacity-50 transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-20 bg-stone-50 border border-stone-100 rounded-lg">
            <p className="text-stone-500">{t("common.no_products")}</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
